import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, ReplaySubject, retry, Subject, Subscription, take, takeUntil } from 'rxjs';

import {
	ApolloCacheManager,
	apolloErrorContains,
	nonRetryableErrorSubStrings,
	SubscriptionManager,
	SubscriptionManagerConfigCreate
} from '@shure/cloud/shared/apollo';
import { Space } from '@shure/cloud/shared/models/devices';
import { monitorLoginState, OktaInterfaceService } from '@shure/cloud/shared/okta/data-access';
import { ILogger } from '@shure/shared/angular/utils/logging';

import { SpacesApiService } from '../api/spaces-api.service';

import {
	SpaceFragment,
	SpaceSubscriptionGQL,
	SpacesQueryGQL,
	SpacesSubscriptionGQL
} from './graphql/generated/cloud-sys-api';
import { mapSpaceFromSysApi } from './mappers/map-space';

const PAGE_SIZE = 1000;

@Injectable()
export class SysApiSpacesApiService implements SpacesApiService {
	public readonly spacesLoadingComplete$ = new BehaviorSubject<boolean>(false);
	public readonly spaceAdded$ = new Subject<Space>();
	public readonly spaceRemoved$ = new Subject<string>();
	public readonly spaceSnapshot$ = new Subject<Space>();

	private readonly spaceIds = new Set<string>();
	private destroy$ = new Subject<void>();
	private isInitialized = false;
	private readonly logger: ILogger;
	private readonly subscriptionReady$ = new ReplaySubject<void>(1);

	private readonly spaceSubscriptionManager = new SubscriptionManager({
		subscriptionType: 'spaces',
		create: (config): Subscription => this.createSpaceSubscription(config),
		retryWaitMs: 10_000,
		maxRetryAttempts: 3
	});

	constructor(
		logger: ILogger,
		oktaService: OktaInterfaceService,
		private readonly spacesQueryGQL: SpacesQueryGQL,
		private readonly spacesSubscriptionGQL: SpacesSubscriptionGQL,
		private readonly spaceSubscriptionGQL: SpaceSubscriptionGQL,
		private readonly apolloCacheManager: ApolloCacheManager
	) {
		this.logger = logger.createScopedLogger('SysApiSpacesApiService');

		monitorLoginState(oktaService, {
			onLogIn: this.initService.bind(this),
			onLogOut: this.suspendService.bind(this)
		});
	}

	public getCachedSpaces$(): Observable<Space[]> {
		const apolloCache = this.apolloCacheManager.getApolloCache('sysApi');
		if (!apolloCache) {
			this.logger.error('getCachedSpaces', 'Apollo cache "sysApi" not found');
			return of([]);
		}

		const cachedSpaces: Space[] = [];
		for (const id of this.spaceIds) {
			const cachedSpace = apolloCache.readFragment<SpaceFragment>({
				// eslint-disable-next-line @typescript-eslint/naming-convention
				id: apolloCache.identify({ __typename: 'Space', id }),
				fragment: SpaceFragment
			});

			const space = mapSpaceFromSysApi(cachedSpace);
			if (space) {
				cachedSpaces.push(space);
			}
		}

		return of(cachedSpaces);
	}

	private initService(): void {
		if (this.isInitialized) {
			return;
		}

		this.isInitialized = true;
		this.destroy$ = new Subject();

		this.spacesLoadingComplete$.next(false);
		this.subscribeToSpaceEvents();
		this.subscriptionReady$.pipe(take(1), takeUntil(this.destroy$)).subscribe(() => {
			this.queryAllSpaces(null);
		});
	}

	private suspendService(): void {
		this.isInitialized = false;
		this.spacesLoadingComplete$.next(true);
		this.destroy$.next();
		this.destroy$.complete();
		this.spaceSubscriptionManager.deregisterAll();
	}

	private subscribeToSpaceEvents(): void {
		this.spacesSubscriptionGQL
			.subscribe({ variables: {}, fetchPolicy: 'network-only' })
			.pipe(retry({ count: 2, delay: 10_000 }), takeUntil(this.destroy$))
			.subscribe({
				next: (result) => {
					const event = result.data?.spaces;

					if (!event) {
						return;
					}

					if ('created' in event) {
						const space = mapSpaceFromSysApi(event.created);
						if (space) {
							this.spaceAdded$.next(space);
							this.spaceSubscriptionManager.register([space.id]);
							this.spaceIds.add(space.id);
						}
					} else if ('deleted' in event) {
						this.spaceSubscriptionManager.deregister(event.deleted);
						this.spaceRemoved$.next(event.deleted);
						this.spaceIds.delete(event.deleted);
					} else if ('ready' in event) {
						this.subscriptionReady$.next();
					}
				},
				error: (error: unknown) => {
					this.logger.error('subscribeToSpaceEvents', 'spaces subscription error', error);
				}
			});
	}

	private queryAllSpaces(afterCursor: string | null): void {
		this.spacesQueryGQL
			.fetch({
				variables: { paginationArgs: { first: PAGE_SIZE, after: afterCursor } },
				fetchPolicy: 'network-only'
			})
			.pipe(retry({ count: 2, delay: 5_000 }), take(1), takeUntil(this.destroy$))
			.subscribe({
				next: (result) => {
					const connection = result.data?.spacesConnection;

					if (!connection) {
						return;
					}

					for (const edge of connection.edges) {
						if (edge.node) {
							const space = mapSpaceFromSysApi(edge.node);
							if (space) {
								this.spaceAdded$.next(space);
								this.spaceSubscriptionManager.register([space.id]);
								this.spaceIds.add(space.id);
							}
						}
					}

					if (connection.pageInfo.hasNextPage && connection.pageInfo.endCursor) {
						this.queryAllSpaces(connection.pageInfo.endCursor);
					} else {
						this.spacesLoadingComplete$.next(true);
					}
				},
				error: (error: unknown) => {
					this.logger.error('queryAllSpaces', 'failed to query spaces', error);
					this.spacesLoadingComplete$.next(true);
				}
			});
	}

	private createSpaceSubscription({
		id,
		retryCallback,
		eventObserver
	}: SubscriptionManagerConfigCreate): Subscription {
		return this.spaceSubscriptionGQL
			.subscribe({
				variables: {
					id
				},
				errorPolicy: 'ignore',
				fetchPolicy: 'network-only'
			})
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (result) => {
					const spaceData = result.data?.nodeChanges;

					if (spaceData && '__typename' in spaceData && spaceData.__typename === 'Space') {
						this.handleSpaceSnapshot(spaceData as SpaceFragment);
					}

					if (eventObserver) {
						eventObserver.eventReceived();
					}
				},
				complete: () => {
					this.logger.error(
						'spaceSubscriptionGQL',
						'subscription completed. Will retry.',
						JSON.stringify({ nodeId: id })
					);
					retryCallback();
				},
				error: (error: unknown) => {
					if (apolloErrorContains(error, nonRetryableErrorSubStrings)) {
						return;
					}
					this.logger.error(
						'spaceSubscriptionGQL',
						'Encountered retryable error',
						JSON.stringify({ id, error }, null, 3)
					);
					retryCallback();
				}
			});
	}

	private handleSpaceSnapshot(spaceFragment: SpaceFragment): void {
		const updatedSpace = mapSpaceFromSysApi(spaceFragment);

		if (!updatedSpace) {
			return;
		}
		this.spaceSnapshot$.next(updatedSpace);
	}
}
